import React, { useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { PlayerEventType, type PlayerStatusChangedEvent } from '@accurate-player/accurate-player-core';
import type { SegmentPlayer } from '@accurate-player/accurate-player-segment';
import { useAppSelector } from '@/redux/hooks';
import { selectIsPlaying } from '@/redux/selectors/packagingSelectors';
import { PLAYER_CONTROLS_CLASSNAME, PLAYER_CONTROLS_ZINDEX } from '@/components/Packaging/constants';
import PlayPauseButton from '@/components/Packaging/VideoPreview/controls/PlayPauseButton';
import MuteButton from '@/components/Packaging/VideoPreview/controls/MuteButton';
import CurrentFrameIndicator from '@/components/Packaging/VideoPreview/controls/CurrentFrameIndicator';
import SeekBar from '@/components/Packaging/VideoPreview/controls/SeekBar';
import FullscreenButton from '@/components/Packaging/VideoPreview/controls/FullscreenButton';

interface Props {
  player: SegmentPlayer;
  videoContainer: HTMLDivElement;
}

const PlayerControls: React.FC<Props> = ({ player, videoContainer }) => {
  const isPlaying = useAppSelector(selectIsPlaying);
  const [isMuted, setIsMuted] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const handlePlayPauseButtonClick = useCallback(async () => {
    await player.api.togglePlay();
  }, [player]);

  const handleMuteButtonClick = useCallback(() => {
    player.api.toggleMute();
  }, [player]);

  const handleFullscreenButtonClick = useCallback(async () => {
    if (isFullscreen) {
      await document.exitFullscreen().then(() => setIsFullscreen(false));
    } else {
      await videoContainer.requestFullscreen().then(() => setIsFullscreen(true));
    }
  }, [videoContainer, isFullscreen]);

  useEffect(() => {
    if (!player) return;

    const handleStatusChange = (event: PlayerStatusChangedEvent) => {
      const { status } = event;
      const isMutedNew = status.muted;
      if (isMuted !== isMutedNew) {
        setIsMuted(status.muted);
      }
    };

    player.on(PlayerEventType.StatusChanged, handleStatusChange);

    return () => {
      if (!player) return;
      player.off(PlayerEventType.StatusChanged, handleStatusChange);
    };
  }, [player, isMuted]);

  useEffect(() => {
    const fullscreenChangeHandler = () => {
      if (document.fullscreenElement) {
        setIsFullscreen(true);
      } else {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', fullscreenChangeHandler);

    return () => {
      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
    };
  }, []);

  return (
    <Wrapper className={PLAYER_CONTROLS_CLASSNAME} data-prevent-video-container-click>
      <PlayPauseButton isPlaying={isPlaying} onClick={handlePlayPauseButtonClick} />
      <CurrentFrameIndicator />
      <SeekBar player={player} />
      <MuteButton isMuted={isMuted} onClick={handleMuteButtonClick} />
      <FullscreenButton isFullscreen={isFullscreen} onClick={handleFullscreenButtonClick} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: absolute;
  z-index: ${PLAYER_CONTROLS_ZINDEX};
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(3, 1, 1)};
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%);
`;

export default PlayerControls;
